import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Check, Undo2, Sparkles, Loader2 } from 'lucide-react';
import { Button, ICON_PX } from '../ui';

const SUGGESTED_PROMPTS = [
  'Add a parallel approval track',
  'Set a 48h SLA on the first step',
  'Add a swimlane for charge nurse',
  'Add a new review task before the end',
];

/**
 * AI Chat panel for workflow generation and modification.
 * Lives in the right sidebar when no node is selected.
 */
function WorkflowAIChat({
  messages = [],
  isLoading = false,
  onSendMessage,
  onApply,
  onRevert,
  mode = 'build', // 'build' | 'execute'
}) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    onSendMessage?.(input.trim());
    setInput('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedPrompt = (prompt) => {
    onSendMessage?.(prompt);
  };

  return (
    <div className="flex flex-col h-full border-l border-slate-200 bg-white rounded-r-xl">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 shrink-0">
        <div className="w-6 h-6 rounded-full bg-[var(--aqua)]/10 flex items-center justify-center">
          <Sparkles size={12} className="text-[var(--aqua)]" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-text-dark">Workflow Assistant</span>
          <span className="text-[10px] text-slate-400">
            {mode === 'build' ? 'Describe changes or ask for help' : 'Ask about progress or status'}
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
        {messages.length === 0 ? (
          /* Empty state with suggested prompts */
          <div className="flex flex-col items-center justify-center h-full text-center px-2">
            <div className="w-10 h-10 rounded-full bg-[var(--aqua)]/10 flex items-center justify-center mb-3">
              <Bot size={20} className="text-[var(--aqua)]" />
            </div>
            <p className="text-sm text-slate-500 mb-4">
              {mode === 'build'
                ? 'I can help you build and modify workflows. Try one of these:'
                : 'Ask me about workflow progress or what needs attention.'}
            </p>
            <div className="space-y-1.5 w-full">
              {SUGGESTED_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => handleSuggestedPrompt(prompt)}
                  className="w-full text-left px-3 py-2 text-xs text-slate-600 bg-slate-50 hover:bg-[var(--aqua)]/5 hover:text-[var(--aqua)] rounded-lg border border-slate-100 hover:border-[var(--aqua)]/30 transition-colors"
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* Message list */
          <>
            {messages.map((msg) => (
              <div key={msg.id} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-[var(--aqua)]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Bot size={12} className="text-[var(--aqua)]" />
                  </div>
                )}
                <div
                  className={[
                    'max-w-[85%] rounded-xl px-3 py-2 text-sm',
                    msg.role === 'user'
                      ? 'bg-[var(--aqua)] text-white'
                      : msg.isError
                        ? 'bg-red-bg text-[var(--red-critical)] border border-[var(--red-critical)]/20'
                        : 'bg-slate-50 text-text-dark border border-slate-100',
                  ].join(' ')}
                >
                  <p className="whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>

                  {/* Apply/Revert controls */}
                  {msg.hasPendingChanges && (
                    <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-200">
                      <Button variant="save" size="sm" onClick={onApply} className="flex-1">
                        <Check size={ICON_PX.sm} className="mr-1" /> Apply
                      </Button>
                      <Button variant="ghost" size="sm" onClick={onRevert} className="flex-1">
                        <Undo2 size={ICON_PX.sm} className="mr-1" /> Revert
                      </Button>
                    </div>
                  )}

                  {/* Applied/Reverted badges */}
                  {msg.applied && (
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-green-nn">
                      <Check size={10} /> Applied
                    </div>
                  )}
                  {msg.reverted && (
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-slate-400">
                      <Undo2 size={10} /> Reverted
                    </div>
                  )}
                </div>
                {msg.role === 'user' && (
                  <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
                    <User size={12} className="text-slate-500" />
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className="w-6 h-6 rounded-full bg-[var(--aqua)]/10 flex items-center justify-center shrink-0">
                  <Bot size={12} className="text-[var(--aqua)]" />
                </div>
                <div className="bg-slate-50 rounded-xl px-3 py-2 border border-slate-100">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 size={14} className="animate-spin" />
                    <span>Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="shrink-0 px-3 pb-3 pt-1">
        <div className="flex items-end gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 focus-within:border-[var(--aqua)] focus-within:ring-1 focus-within:ring-[var(--aqua)]/30">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={mode === 'build' ? 'Describe a change...' : 'Ask about status...'}
            rows={1}
            className="flex-1 bg-transparent text-sm text-text-dark placeholder:text-slate-400 resize-none focus:outline-none min-h-[20px] max-h-[80px]"
            style={{ lineHeight: '20px' }}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className={[
              'p-1.5 rounded-lg transition-colors shrink-0',
              input.trim() && !isLoading
                ? 'bg-[var(--aqua)] text-white hover:bg-[var(--aqua)]/90'
                : 'bg-slate-200 text-slate-400',
            ].join(' ')}
          >
            <Send size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default WorkflowAIChat;
